import "./SearchLocation.scss"

import React, { FunctionComponent, useCallback, useState, useMemo, useEffect, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Flex } from "components/Flex"
import { Coordinates } from "@/types"
import LocationService from "services/LocationService"
import Text from "components/Text/Text"
import FormField from "components/FormField/FormField"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import InputSearch from "components/InputSearch/InputSearch"
import { IMapMarkerProps, MapMarkerType } from "components/MapView/MapMarker/MapMarker"
import MapView, { MapViewControl } from "components/MapView/MapView"
import { GroupedControl } from "config/constants"
import Select from "components/Select/Select"
import { Grid } from "components/Grid"
import { TenantWarehouse } from "models/TenantWarehouse"

interface ISearchLocationProps {
    modes?: SearchLocationMode[]
    tenantWarehouses?: TenantWarehouse[]
    preferredWarehouse?: TenantWarehouse
    preselectedWarehouse?: TenantWarehouse | null
    searchFieldLabel?: string
    toolbar?: ReactNode
    initSearch?: string
    initMarkerCoordinates?: Coordinates | null
    additionalMarkers?: IMapMarkerProps[]
    markerType?: MapMarkerType
    mapHeight?: string
    showCoordinates?: boolean
    isEditable?: boolean
    onChangeMarkerCoordinates?: (c: Coordinates | null) => void,
}

export enum SearchLocationMode {
    warehouse = "warehouse",
    externalLocation = "externalLocation",
}

const SearchLocation: FunctionComponent<React.PropsWithChildren<ISearchLocationProps>> = ({
    modes = [SearchLocationMode.externalLocation],
    tenantWarehouses,
    preferredWarehouse,
    preselectedWarehouse,
    initMarkerCoordinates: initMarkerCoordinatesProp,
    additionalMarkers,
    initSearch,
    searchFieldLabel,
    toolbar,
    markerType,
    mapHeight,
    showCoordinates,
    isEditable,
    onChangeMarkerCoordinates = () => { }
}) => {
    const { t } = useTranslation()
    const [mode, setMode] = useState<SearchLocationMode | null>(modes[0] || SearchLocationMode.externalLocation)
    const [selectedWarehouse, setSelectedWarehouse] = useState<TenantWarehouse | null>(null)
    const [markerCoordinates, setMarkerCoordinates] = useState<Coordinates | null>(initMarkerCoordinatesProp || null)
    const [initMarkerCoordinates, setInitMarkerCoordinates] = useState<Coordinates | null>(initMarkerCoordinatesProp || null)
    const [errorMessage, setErrorMessage] = useState("")
    const [hasChangedCoordinates, setHasChangedCoordinates] = useState(false)
    const autoCenterToMarkers = !!additionalMarkers && !hasChangedCoordinates
    const canSelectLocationOnMap = isEditable && (!preselectedWarehouse || mode !== SearchLocationMode.warehouse)

    const handleChangeMarkerCoordinates = useCallback((coords: Coordinates | null) => {
        onChangeMarkerCoordinates(coords)
        setHasChangedCoordinates(true)
    }, [onChangeMarkerCoordinates])

    const handleOnReset = useCallback(() => {
        setSelectedWarehouse(null)
        setErrorMessage("")
        setMarkerCoordinates(null)
        setInitMarkerCoordinates(null)
        handleChangeMarkerCoordinates(null)
    }, [handleChangeMarkerCoordinates])

    const handleWarehouseSelect = useCallback((warehouse?: TenantWarehouse) => {
        if (!warehouse) {
            handleOnReset()
            return
        }
        setErrorMessage("")
        const warehouseCoordinates = {
            lat: warehouse.location.lat,
            lng: warehouse.location.lng
        }
        setMarkerCoordinates(warehouseCoordinates)
        setInitMarkerCoordinates(warehouseCoordinates)
        handleChangeMarkerCoordinates(warehouseCoordinates)
        setSelectedWarehouse(warehouse)
        setHasChangedCoordinates(false)
    }, [handleChangeMarkerCoordinates, handleOnReset])

    useEffect(() => {
        if (!hasChangedCoordinates && modes.includes(SearchLocationMode.warehouse) && !initMarkerCoordinates && preferredWarehouse) {
            setMode(SearchLocationMode.warehouse)
            handleWarehouseSelect(preferredWarehouse)
        }
    }, [handleWarehouseSelect, hasChangedCoordinates, initMarkerCoordinates, modes, preferredWarehouse, tenantWarehouses])

    useEffect(() => {
        if (!preselectedWarehouse) return
        setMode(SearchLocationMode.warehouse)
        setSelectedWarehouse(preselectedWarehouse)
    }, [preselectedWarehouse])

    useEffect(() => {
        setMarkerCoordinates(initMarkerCoordinatesProp || null)
        setInitMarkerCoordinates(initMarkerCoordinatesProp || null)
    }, [initMarkerCoordinatesProp])

    useEffect(() => {
        setErrorMessage("")
    }, [mode])

    const markers = useMemo<IMapMarkerProps[]>(() => {
        return [
            ...additionalMarkers || [],
            ...markerCoordinates ? [{
                key: "" + markerCoordinates.lat + markerCoordinates.lng,
                coordinates: markerCoordinates,
                type: markerType,
                isDraggable: canSelectLocationOnMap,
                onDragEnd: onMarkerDragEnd,
            }] : [],
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSelectLocationOnMap, markerCoordinates, markerType, additionalMarkers])

    async function handleOnSearch(value: string) {
        setErrorMessage("")
        setSelectedWarehouse(null)
        const trimmedValue = value.trim()
        if (!trimmedValue) return
        const foundCoordinates = await LocationService.getCoordinates(value)
        if (!foundCoordinates) {
            setErrorMessage(t('validation.locationNotFound'))
            return
        }
        setMarkerCoordinates(foundCoordinates)
        setInitMarkerCoordinates(foundCoordinates)
        handleChangeMarkerCoordinates(foundCoordinates)
        setHasChangedCoordinates(false)
    }

    const onMapClick = useCallback(coordinates => {
        if (!canSelectLocationOnMap) return
        setMarkerCoordinates(coordinates)
        handleChangeMarkerCoordinates(coordinates)
        if (selectedWarehouse) {
            setSelectedWarehouse(null)
        }
    }, [canSelectLocationOnMap, handleChangeMarkerCoordinates, selectedWarehouse])

    function onMarkerDragEnd(coordinates: Coordinates) {
        setMarkerCoordinates(coordinates)
        handleChangeMarkerCoordinates(coordinates)
        if (selectedWarehouse) {
            setSelectedWarehouse(null)
        }
    }

    useEffect(() => {
        if (!initSearch) return
        handleOnSearch(initSearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initSearch])


    function getSearchLocationModeLabel(searchLocationMode: SearchLocationMode) {
        switch (searchLocationMode) {
            case SearchLocationMode.externalLocation:
                return t('searchLocationMode.externalLocation')
            case SearchLocationMode.warehouse:
                return t('searchLocationMode.warehouse')
            default:
                return ""
        }
    }

    return (
        <Flex className={` search-location ${mapHeight ? '' : 'h-100'}`}
            flow="column">
            {isEditable &&
                <div className="mb-3">
                    <FormField
                        label={searchFieldLabel}
                        after={toolbar}>
                        <Grid
                            columns={modes.length > 1 ? "minmax(0, 1fr) minmax(0, 2fr)" : "1fr"}>
                            {modes.length > 1 &&
                                <Select
                                    options={modes.map(m => ({
                                        label: getSearchLocationModeLabel(m),
                                        value: m,
                                    }))}
                                    value={mode}
                                    onChange={option => setMode(option?.value || null)}
                                    grouped={GroupedControl.left}
                                />
                            }
                            {mode === SearchLocationMode.externalLocation &&
                                <InputSearch
                                    value={initSearch}
                                    placeholder={t('common.searchInMap')}
                                    onChange={handleOnSearch}
                                    onReset={handleOnReset}
                                    grouped={modes.length > 1 ? GroupedControl.right : undefined}
                                    helpTooltip={{
                                        maxWidth: "450px",
                                        content: (
                                            <Text>
                                                {t('common.searchLocationTooltip.line1')}
                                                <ul>
                                                    <li>{t('common.searchLocationTooltip.line2')}</li>
                                                    <li>{t('common.searchLocationTooltip.line3')}</li>
                                                </ul>
                                            </Text>
                                        )
                                    }}
                                    data-testid="location-search-input" />
                            }
                            {mode === SearchLocationMode.warehouse &&
                                <Select
                                    options={tenantWarehouses?.map(warehouse => ({
                                        label: warehouse.description,
                                        value: warehouse,
                                    }))}
                                    value={selectedWarehouse}
                                    isDisabled={!tenantWarehouses}
                                    placeholder={tenantWarehouses ? undefined : t('common.loading')}
                                    grouped={modes.length > 1 ? GroupedControl.right : undefined}
                                    onChange={option => handleWarehouseSelect(option?.value)}
                                    isClearable
                                />
                            }
                        </Grid>
                    </FormField>
                    {errorMessage &&
                        <ValidationMessage>{errorMessage}</ValidationMessage>
                    }
                </div>
            }
            <div className="search-location__map"
                style={{ height: mapHeight }}>
                <MapView
                    markers={markers}
                    initCenter={!autoCenterToMarkers ? (!hasChangedCoordinates ? initMarkerCoordinates || undefined : undefined) : undefined}
                    autoCenterToMarkers={autoCenterToMarkers}
                    autoCenterToUserLocation={hasChangedCoordinates ? false : !initSearch && !initMarkerCoordinates}
                    onMapClick={onMapClick}
                    controls={[
                        MapViewControl.type,
                        MapViewControl.zoom,
                    ]}
                    info={(showCoordinates || null) && markerCoordinates &&
                        <>
                            {markerCoordinates.lat} {markerCoordinates.lng}<br />
                            {LocationService.decimalCoordinatesToDegreesString(markerCoordinates)}
                        </>
                    }
                />
            </div>
        </Flex>

    )
}

export default SearchLocation
